// Client-side validation for the SMTP transport form — kept out of the
// component so every rule is testable without a DOM.
//
// These rules MIRROR `UpsertEmailTransportDto` (services/api/src/integrations/
// email/dto/upsert-email-transport.dto.ts) plus the Gmail App Password shape
// from plan §4. They are a courtesy, not the gate: the server validates again.

import type { EmailTransportProvider, UpsertEmailTransportInput } from '@bbpos/types';
import { GMAIL_APP_PASSWORD_LENGTH } from '@bbpos/types';

/** What the form holds while it is being typed into — every field a string. */
export interface EmailTransportDraft {
  provider: EmailTransportProvider;
  email: string;
  password: string;
  from_name: string;
  reply_to_email: string;
  smtp_host: string;
  smtp_port: string;
}

export type EmailTransportField = keyof EmailTransportDraft;

export type EmailTransportErrors = Partial<Record<EmailTransportField, string>>;

export type ValidationResult =
  | { ok: true; input: UpsertEmailTransportInput }
  | { ok: false; errors: EmailTransportErrors };

const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** RFC 1123 hostname labels or an IPv4 literal — same pattern as the DTO. */
const HOST_REGEX =
  /^(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)*[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?$/;

const GMAIL_APP_PASSWORD_REGEX = new RegExp(`^[a-z]{${GMAIL_APP_PASSWORD_LENGTH}}$`, 'i');

/**
 * Google shows App Passwords as four space-separated groups ("abcd efgh ijkl
 * mnop") and users paste them that way; the mailbox expects the 16 letters.
 * Strips every kind of whitespace, including the non-breaking space a copy out
 * of the Google page sometimes carries.
 */
export function normalizeGmailAppPassword(raw: string): string {
  return raw.replace(/\s/g, '');
}

export function validateEmailTransportInput(draft: EmailTransportDraft): ValidationResult {
  const errors: EmailTransportErrors = {};

  const email = draft.email.trim();
  if (!email) errors.email = 'Sender email is required';
  else if (!EMAIL_REGEX.test(email)) errors.email = 'Enter a valid email address';
  else if (email.length > 255) errors.email = 'Email must be 255 characters or less';

  const isGmail = draft.provider === 'gmail';
  const password = isGmail ? normalizeGmailAppPassword(draft.password) : draft.password;
  if (!password) {
    errors.password = isGmail ? 'App Password is required' : 'Password is required';
  } else if (isGmail && !GMAIL_APP_PASSWORD_REGEX.test(password)) {
    errors.password = `A Gmail App Password is ${GMAIL_APP_PASSWORD_LENGTH} letters (spaces are ignored)`;
  } else if (!isGmail && password.length > 256) {
    errors.password = 'Password must be 256 characters or less';
  }

  const host = draft.smtp_host.trim();
  const portRaw = draft.smtp_port.trim();
  let port = 0;
  if (!isGmail) {
    if (!host) errors.smtp_host = 'SMTP host is required';
    else if (host.length > 255) errors.smtp_host = 'Host must be 255 characters or less';
    else if (!HOST_REGEX.test(host)) errors.smtp_host = 'Enter a hostname or IPv4 address';

    if (!portRaw) {
      errors.smtp_port = 'SMTP port is required';
    } else if (!/^\d+$/.test(portRaw)) {
      errors.smtp_port = 'Port must be a whole number';
    } else {
      port = Number(portRaw);
      if (port < 1 || port > 65535) errors.smtp_port = 'Port must be between 1 and 65535';
    }
  }

  const fromName = draft.from_name.trim();
  if (fromName.length > 255) errors.from_name = 'Display name must be 255 characters or less';

  const replyTo = draft.reply_to_email.trim();
  if (replyTo && !EMAIL_REGEX.test(replyTo)) {
    errors.reply_to_email = 'Enter a valid Reply-To address';
  } else if (replyTo.length > 255) {
    errors.reply_to_email = 'Reply-To must be 255 characters or less';
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  const input: UpsertEmailTransportInput = {
    provider: draft.provider,
    email,
    password,
  };
  if (fromName) input.from_name = fromName;
  if (replyTo) input.reply_to_email = replyTo;
  // Gmail is the fixed smtp.gmail.com:465 preset — the server rejects an
  // overriding host/port, so they are never sent for it.
  if (!isGmail) {
    input.smtp_host = host;
    input.smtp_port = port;
  }

  return { ok: true, input };
}

/** A blank draft, optionally seeded from the saved config (never the password). */
export function emptyDraft(
  seed?: {
    provider?: EmailTransportProvider;
    email?: string;
    from_name?: string | null;
    reply_to_email?: string | null;
    smtp_host?: string;
    smtp_port?: number;
  } | null,
): EmailTransportDraft {
  const provider = seed?.provider ?? 'gmail';
  return {
    provider,
    email: seed?.email ?? '',
    password: '',
    from_name: seed?.from_name ?? '',
    reply_to_email: seed?.reply_to_email ?? '',
    smtp_host: provider === 'smtp' ? (seed?.smtp_host ?? '') : '',
    smtp_port: provider === 'smtp' && seed?.smtp_port ? String(seed.smtp_port) : '',
  };
}
